import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { string, number, bool, func } from 'prop-types';
import _ from 'lodash';
import '../index.scss';
import Input from '../../../common/Input';
import DateInput from '../../../common/DateInput';
import PasswordInput from '../../../common/PasswordInput';
import {
  SIGNUP_STATE,
  DEFAULT_PASSWORD_STATE,
  INTELLIMIZE_LOYALTY_SIGN_UP_EVENT,
  INTELLIMIZE_GENIUS_SIGN_UP_EVENT
} from '../../../../constants/defaults';
import { Errors } from '../../../../constants/errors';
import { signUp } from '../../../../services/Api/User/UserService';
import { useSelector } from 'react-redux';
import CheckMarks from '../../../common/CheckMark';

const SignupHomeDelivery = ({
  email,
  isFromHomeDelivery,
  hideAuthenticateModal,
  hideAuthenticateModalAfterSignup
}) => {
  const { t, i18n } = useTranslation();
  let lang = i18n.language;
  const signupState = {
    ...SIGNUP_STATE,
    Email: email
  };
  const [signUpData, setSignUpData] = useState({
    ...signupState,
    ...DEFAULT_PASSWORD_STATE
  });
  const modalState = useSelector(state => state.modal.modal);
  const [showLoader, setLoader] = useState(false);
  const [apiError, setApiError] = useState('');
  const [checkMarkValue, setCheckMarkValue] = useState(true);
  const savedLinkClicked = useSelector(
    state => state.userDrugs.savedLinkClicked
  );
  const savedDrugClicked = useSelector(
    state => state.userDrugs.savedDrugClicked
  );
  const currentCouponData = useSelector(state => state.userDrugs.couponData);

  let signupPath;
  let signupPathInvite;

  const {
    FName,
    FNameErr,
    LName,
    LNameErr,
    birthDate,
    birthDateErr,
    password,
    passwordErr,
    apiCommonValidationError
  } = signUpData;

  const handleInputChange = event => {
    const newState = { ...signUpData };
    _.handleInputChange(event, setSignUpData, newState);
  };

  const handleInputBlur = event => {
    const { name, value } = event.target;
    if (name === 'birthDate') {
      const ageError = _.calculateAgeNew(value);
      switch (ageError) {
        case 'Invalid':
          handleError(name, 'Date');
          break;
        case 'Greater':
          handleError(name, 'MaxAge');
          break;
        case true:
          handleError(name, 'Age');
          break;
        default:
          handleError(name, '');
      }
    } else if (
      (name === 'FName' || name === 'LName') &&
      !_.validateName(value)
    ) {
      handleError(name, name);
    } else {
      handleError(name, '');
    }
  };

  const handleError = (name, type) => {
    _.setError(setSignUpData, signUpData, name, type);
  };

  const onPwdInputChange = event => {
    const { name, value } = event.target;
    setSignUpData({
      ...signUpData,
      [name]: value,
      passwordErr: Errors['']
    });
  };

  const onPwdInputBlur = event => {
    const { name, value } = event.target;
    setSignUpData({
      ...signUpData,
      [`${name}Err`]: _.validatePassword(value),
      apiCommonValidationError: false
    });
  };

  const handleFormSubmit = e => {
    setSignUpData({
      ...signUpData,
      apiCommonValidationError: false
    });
    const newErrorState = {};
    newErrorState.FNameErr = !_.validateName(FName.trim()) ? Errors.FName : '';
    newErrorState.LNameErr = !_.validateName(LName.trim()) ? Errors.LName : '';
    newErrorState.passwordErr = _.validatePassword(password);
    const ageError = _.calculateAgeNew(birthDate);
    switch (ageError) {
      case 'Invalid':
        newErrorState.birthDateErr = Errors.Date;
        break;
      case 'Greater':
        newErrorState.birthDateErr = Errors.MaxAge;
        break;
      case true:
        newErrorState.birthDateErr = Errors.Age;
        break;
      default:
        newErrorState.birthDateErr = '';
    }
    if (
      newErrorState.FNameErr ||
      newErrorState.LNameErr ||
      newErrorState.passwordErr ||
      newErrorState.birthDateErr
    ) {
      setSignUpData({
        ...signUpData,
        ...newErrorState
      });
      return;
    } else {
      setSignUpData({
        ...signUpData,
        ...newErrorState
      });
      handleSubmit(e);
    }
  };

  const sendIntellimizeEvent = (eventName, value = {}) => {
    try {
      window.intellimize.sendEvent(eventName, value);
    } catch (e) {
      console.log('err while sending event:', e);
    }
  };

  const handleSubmit = async () => {
    setLoader(true);
    if (savedLinkClicked) {
      signupPath = { loyaltySignupPath: 'coupon modal - save prompt' };
    } else if (savedDrugClicked) {
      signupPath = { loyaltySignupPath: 'save drug' };
    } else if (!_.isEmpty(currentCouponData.item)) {
      signupPath = { loyaltySignupPath: 'coupon modal - save coupon' };
    } else {
      let { signUpPathWithInvite } = _.getSignUpPath(modalState);
      signupPath = signUpPathWithInvite;
      signupPathInvite = signUpPathWithInvite;
    }
    _.triggerGTMVirtualPageViewEvent({
      event: 'signUpSubmitClick',
      formId: 'sign up',
      clickText: 'Sign up',
      ...signupPath
    });

    const splitDate = !_.isEmpty(birthDate) && birthDate.split('/');
    const apiDate = `${splitDate[2]}/${splitDate[0]}/${splitDate[1]}`;
    const requestSignUpData = {
      firstName: FName.trim(),
      lastName: LName.trim(),
      email: email.trim(),
      birthDate: apiDate,
      password,
      tenantId: 0,
      acceptAgreement: true,
      subSource: isFromHomeDelivery ? 'sc-geniusrx' : 'sc-singlecare',
      userMarketingConsent: checkMarkValue
    };
    try {
      const signupResponse = await signUp(requestSignUpData);
      if (signupResponse.data && !_.isEmpty(signupResponse.data)) {
        hideAuthenticateModalAfterSignup(signupResponse);
        if (isFromHomeDelivery) {
          sendIntellimizeEvent(INTELLIMIZE_GENIUS_SIGN_UP_EVENT);
        } else {
          sendIntellimizeEvent(INTELLIMIZE_LOYALTY_SIGN_UP_EVENT);
          _.triggerGTMVirtualPageViewEvent({
            event: 'signUpSuccess',
            formId: 'sign up',
            ...signupPathInvite
          });
          _.triggerGTMVirtualPageViewEvent({
            event: 'signUpSuccessRakuten',
            formId: 'sign up'
          });
        }
      }
    } catch (err) {
      const {
        response: { data }
      } = err;
      if (data && data.error) {
        if (
          data.error == 'ValidationErrCommonPassword' ||
          data.error == 'ValidationErrInvalidPassword'
        ) {
          data.error == 'ValidationErrCommonPassword'
            ? setSignUpData({
                ...signUpData,
                passwordErr: true,
                apiCommonValidationError: true
              })
            : setSignUpData({
                ...signUpData,
                passwordErr: true,
                apiCommonValidationError: false
              });
        } else {
          setSignUpData({
            ...signUpData,
            passwordErr: '',
            apiCommonValidationError: false
          });
          setApiError('Something went wrong.');
        }
      } else {
        setApiError(data ? data.message : 'Something went wrong.');
        setTimeout(() => {
          setApiError('');
        }, 4000);
      }
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="signupHomeDelivery">
      <div className="newdeliverydata">
        {isFromHomeDelivery ? (
          <div>
            <h2 id="dialogTitleHeading" className="deliveyHeading">
              <span>
                <img
                  src="/content/images/homeDelivery/step3.png"
                  className="boxImg"
                  alt=""
                />
              </span>
              {t('Get home delivery')}
            </h2>
            <h3>{t('Finish setting up your account.')}</h3>
          </div>
        ) : (
          <div>
            <h2 className="signupLabelText" id="dialogTitleHeading">
              {t('Sign up')}&nbsp;<strong>{t('FREE')}</strong>&nbsp;
              {t('to get')}&nbsp;{t('our lowest prices')}
            </h2>
            <h3>{t('Finish setting up your account.')}</h3>
          </div>
        )}
        <div className="separatorLine"></div>
      </div>
      <p className="paraText newdeliverydataText">
        {t('Your name and date of birth must ')}
        {t('match what is on file at your pharmacy.')}
        <span>{t('All fields are required')}</span>
      </p>
      <form
        className="commonForm"
        onSubmit={e => {
          e.preventDefault();
          handleFormSubmit(e);
        }}
        autoComplete="on"
      >
        <Input
          label={t('First name')}
          name="FName"
          placeholder={t('First name')}
          value={FName}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          error={FNameErr}
          autoComplete="given-name"
          autoFocus={true}
        />
        <Input
          parentClass="float-right"
          label={t('Last name')}
          name="LName"
          placeholder={t('Last name')}
          value={LName}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          error={LNameErr}
          autoComplete="family-name"
        />
        <DateInput
          parentClass="dateInputRow float-left"
          birthDate={birthDate}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          birthDateErr={birthDateErr}
          autoComplete="bday"
        />
        <PasswordInput
          isFromSignupFlow
          parentClass="passwordInput float-right"
          password={password}
          name="password"
          error={passwordErr}
          label="Password"
          onInputChange={onPwdInputChange}
          onInputBlur={onPwdInputBlur}
          showInfoIcon={true}
          showConfirmMatch={false}
          autoComplete="new-password"
          apiCommonValidationError={apiCommonValidationError}
        />

        <div className="helper-text termsLinks">
          <p>
            {lang === 'es' && t('apply')}&nbsp;
            <a
              className="dialogComponent"
              target="_blank"
              rel="noopener noreferrer"
              href={_.withLocale(lang, '/terms-and-conditions')}
            >
              {t('Terms and Conditions')}
            </a>
            &nbsp;{lang === 'en' && `${t('apply')}.`}
          </p>
        </div>

        <CheckMarks
          setCheckMark={value => {
            setCheckMarkValue(value);
          }}
        />

        {!_.isEmpty(apiError) && <p>{apiError}</p>}
        {showLoader ? (
          <img
            src="/content/images/loader/loading.gif"
            alt="loader"
            className="searchBarLoader"
          />
        ) : (
          <button
            type="submit"
            className="dialogComponent button pink"
            aria-describedby="pass_requirements"
          >
            {t('Sign up')}
          </button>
        )}
        <div className="helper-text">
          <div
            tabIndex="0"
            className="dialogComponent linkText whiteFocus closeModalText"
            onClick={() => hideAuthenticateModal()}
            onKeyPress={e => {
              e.preventDefault();
              if (e.key === 'Enter') {
                hideAuthenticateModal();
              }
            }}
          >
            {t('Close')}
          </div>
          <div className="marginBottom-50 mobile-only"></div>
        </div>
      </form>
    </div>
  );
};

SignupHomeDelivery.propTypes = {
  email: string,
  redirectUri: string,
  quantity: number,
  isFromHomeDelivery: bool,
  hideAuthenticateModal: func,
  hideAuthenticateModalAfterSignup: func
};

SignupHomeDelivery.defaultProps = {
  email: '',
  redirectUri: '',
  quantity: 0,
  isFromHomeDelivery: false,
  hideAuthenticateModal: () => {},
  hideAuthenticateModalAfterSignup: () => {}
};

export default SignupHomeDelivery;
